import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multimonthPlugin from '@fullcalendar/multimonth';
import { FullCalendarModule, FullCalendarComponent } from '@fullcalendar/angular';
import { RouteParams } from '@app/shared/abstract/route-params';
import { EventService } from '@app/events/shared/event.service';
import { VenueService } from '@app/venues/shared/venue.service';

type StatusFilter = 'Both' | 'Confirmed' | 'Provisional';

@Component({
  selector: 'app-events-calendar',
  standalone: true,
  templateUrl: './events-calendar.component.html',
  imports: [FullCalendarModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsCalendarComponent implements OnInit {
  @ViewChild('calendar', { static: false }) calendarComponent!: FullCalendarComponent;

  loading = false;
  venues: any[] = [];

  // TVG-158: the calendar lands on both confirmed and provisional events. The
  // user can narrow to a single status; switching is handled client-side so the
  // calendar never blanks or background-loads while toggling.
  statusFilter: StatusFilter = 'Both';

  // Venues toggled off here are hidden from the calendar. Empty means all
  // venues are shown.
  private hiddenVenueIds = new Set<number>();

  // Cache the mapped events for the loaded date range so status/venue toggles
  // re-filter instantly without a network round trip.
  private cachedRangeKey: string | null = null;
  private cachedEvents: any[] = [];

  routeParams = new RouteParams({
    pageNum: 1,
  });

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay,list',
    },
    plugins: [multimonthPlugin, dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
  };

  constructor(
    private eventService: EventService,
    private router: Router,
    private venueService: VenueService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadVenues();
    // Set up calendar with events function that auto-loads data
    this.calendarOptions = {
      ...this.calendarOptions,
      events: (fetchInfo, successCallback, failureCallback) => {
        this.loadEventsForRange(fetchInfo.start, fetchInfo.end, successCallback, failureCallback);
      },
      eventClick: (info) => {
        this.onEventClick(info);
      },
    };
  }

  private loadEventsForRange(start: Date, end: Date, successCallback: any, failureCallback: any) {
    const startDate = start.toISOString().split('T')[0];
    const endDate = end.toISOString().split('T')[0];
    const rangeKey = `${startDate}_${endDate}`;

    // Serve from cache when only a filter changed (range unchanged). This keeps
    // status/venue switching instant and avoids any blank or background loading.
    if (rangeKey === this.cachedRangeKey) {
      successCallback(this.applyFilters(this.cachedEvents));
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    // Always fetch both confirmed and provisional for the range; the visible
    // status set is decided client-side.
    this.eventService.getCalendarEvents(startDate, endDate).subscribe({
      next: (response) => {
        this.loading = false;
        this.cachedEvents = response.data.map((event) => {
          const venueId = event.venue_id ?? event.extendedProps?.venue_id;
          const venueInitial = event.extendedProps?.venue_initial;
          return {
            ...event,
            title: venueInitial ? `[${venueInitial}] ${event.title}` : event.title,
            backgroundColor: this.getVenueColor(venueId),
            borderColor: this.getVenueColor(venueId),
          };
        });
        this.cachedRangeKey = rangeKey;
        successCallback(this.applyFilters(this.cachedEvents));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Calendar: Error loading events:', error);
        this.loading = false;
        this.cachedRangeKey = null;
        failureCallback(error);
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Filter the cached events by the active status and venue toggles.
   */
  private applyFilters(events: any[]): any[] {
    return events.filter((event) => {
      const status = event.extendedProps?.status;
      if (this.statusFilter !== 'Both' && status !== this.statusFilter) {
        return false;
      }

      const venueId = event.extendedProps?.venue_id ?? event.venue_id;
      if (venueId != null && this.hiddenVenueIds.has(venueId)) {
        return false;
      }

      return true;
    });
  }

  private loadVenues() {
    this.venueService.getAll().subscribe({
      next: (response) => {
        this.venues = response.data.map((venue: any, index: number) => ({
          ...venue,
          color: venue.color || this.defaultColor(index),
        }));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Calendar: Error loading venues:', error);
      }
    });
  }

  setStatusFilter(status: StatusFilter) {
    if (this.statusFilter === status) {
      return;
    }
    this.statusFilter = status;
    // Re-filter the cached range; no network request is made.
    this.calendarComponent?.getApi().refetchEvents();
  }

  toggleVenue(venueId: number) {
    if (this.hiddenVenueIds.has(venueId)) {
      this.hiddenVenueIds.delete(venueId);
    } else {
      this.hiddenVenueIds.add(venueId);
    }
    // Re-filter the cached range; no network request is made.
    this.calendarComponent?.getApi().refetchEvents();
  }

  isVenueVisible(venueId: number): boolean {
    return !this.hiddenVenueIds.has(venueId);
  }

  private getVenueColor(venueId: number): string {
    const venue = this.venues.find((v) => v.id === venueId);
    return venue?.color || this.defaultColor(venueId);
  }

  private defaultColor(index: number): string {
    const palette = [
      '#2563EB', // blue
      '#16A34A', // green
      '#DC2626', // red
      '#9333EA', // purple
      '#F97316', // orange
      '#0EA5E9', // cyan
      '#BE123C', // rose
      '#15803D', // dark green
    ];
    return palette[index % palette.length];
  }

  private onEventClick(info: any) {
    const eventId = info.event.id;

    // Open in new tab
    const url = this.router.serializeUrl(this.router.createUrlTree(['/events', eventId]));
    window.open(url, '_blank');
  }


}
